<script lang="ts" setup>
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import VueTurnstile from 'vue-turnstile'

import { useProblemReportStore } from '../stores/problem-report'
import { hasMaxLength, isRequired } from '../utils/validation'
import * as limits from '../utils/limits'
import { websiteLinks } from '../config/site'

const turnstileSiteId = import.meta.env.VITE_TURNSTILE_SITE_KEY

const route = useRoute()
const store = useProblemReportStore()
const { isSending } = storeToRefs(store)

const message = ref('')
const contactEmail = ref('')
const turnstileToken = ref('')
const turnstile = ref<InstanceType<typeof VueTurnstile> | null>(null)
const sent = ref(false)
const turnstileMissing = ref(false)

// The page the visitor came from, so staff know where it happened.
const fromPath = typeof route.query.from === 'string' ? route.query.from.slice(0, 200) : undefined

async function onSubmit() {
  turnstileMissing.value = !turnstileToken.value
  if (turnstileMissing.value) return
  sent.value = await store.send(
    { message: message.value, contactEmail: contactEmail.value, pagePath: fromPath },
    turnstileToken.value
  )
  // A Turnstile token works once and vue-turnstile does not watch its model, so ask the widget for a fresh one.
  if (!sent.value) {
    turnstileToken.value = ''
    turnstile.value?.reset()
  }
}
</script>

<template>
  <q-page padding class="layout-constrained">
    <div class="q-pa-md">
      <h1 class="text-h5 q-mt-none">Report a problem</h1>

      <q-card v-if="sent" role="status" data-testid="problem-report-sent">
        <q-card-section>
          <p class="text-body1">Thanks, your report has been sent to the RoadFootage team.</p>
          <p class="text-body1">
            For common questions, see the <a :href="websiteLinks.help">help centre</a>.
          </p>
          <q-btn color="primary" label="Back to Explore" :to="{ name: 'explore' }" />
        </q-card-section>
      </q-card>

      <q-card v-else>
        <q-card-section>
          <p class="text-body1">
            Tell us what went wrong. To remove content, use the
            <router-link :to="{ name: 'request-removal' }">removal request form</router-link>
            instead.
          </p>
          <q-form class="q-gutter-md" data-testid="problem-report-form" @submit="onSubmit">
            <q-input
              v-model="message"
              type="textarea"
              label="What happened? *"
              autogrow
              outlined
              lazy-rules
              :rules="[isRequired('Tell us what went wrong.'), hasMaxLength(2000)]"
              data-testid="problem-report-message"
            />
            <q-input
              v-model="contactEmail"
              type="email"
              label="Your email (optional, so we can reply)"
              outlined
              dense
              lazy-rules
              :rules="[hasMaxLength(limits.emailAddress)]"
              data-testid="problem-report-email"
            />
            <vue-turnstile
              ref="turnstile"
              v-model="turnstileToken"
              :site-key="turnstileSiteId"
              theme="light"
            />
            <div v-if="turnstileMissing" class="text-negative" role="alert">
              Please wait for the browser check to finish, then send again.
            </div>
            <q-btn
              type="submit"
              color="primary"
              label="Send report"
              :loading="isSending"
              :disable="isSending"
              data-testid="problem-report-submit"
            />
          </q-form>
        </q-card-section>
      </q-card>
    </div>
  </q-page>
</template>
